import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { EntityType } from '../../../../../generated/data-contracts';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../../../../components/HierarchicalFilter';
import { getHierarchicalLevels } from '../utils/entityUtils';

interface EntitySelectorProps {
  entityType: EntityType;
  selectedEntities: any[];
  onEntitiesChange: (entities: any[]) => void;
  hierarchicalSelections: HierarchicalFilterSelections;
  onHierarchicalChange: (selections: HierarchicalFilterSelections) => void;
  selectedDashboardGroup?: number;
  onDashboardGroupChange: (groupId: number) => void;
  // Estados de datos
  clients: any[];
  plants: any[];
  dashboardGroups: any[];
  dashboards: any[];
  alerts: any[];
  diagrams: any[];
  // Estados de carga
  loadingClients: boolean;
  loadingPlants: boolean;
  loadingDashboardGroups: boolean;
  loadingDashboards: boolean;
  loadingAlerts: boolean;
  loadingDiagrams: boolean;
}

export const EntitySelector: React.FC<EntitySelectorProps> = ({
  entityType,
  selectedEntities,
  onEntitiesChange,
  hierarchicalSelections,
  onHierarchicalChange,
  selectedDashboardGroup,
  onDashboardGroupChange,
  clients,
  plants,
  dashboardGroups,
  dashboards,
  alerts,
  diagrams,
  loadingClients,
  loadingPlants,
  loadingDashboardGroups,
  loadingDashboards,
  loadingAlerts,
  loadingDiagrams,
}) => {
  const renderNoOptionsMessage = (
    entityName: string,
    locationName?: string
  ) => (
    <Box
      sx={{
        p: 3,
        textAlign: 'center',
        bgcolor: 'background.default',
        borderRadius: 2,
      }}
    >
      <Typography variant='body1' color='textSecondary' gutterBottom>
        ✅ Todos los {entityName.toLowerCase()} ya tienen permisos asignados
      </Typography>
      <Typography variant='body2' color='textSecondary'>
        No hay {entityName.toLowerCase()} disponibles para agregar
        {locationName ? ` ${locationName}` : ''}
      </Typography>
    </Box>
  );

  switch (entityType) {
    case EntityType.CLIENT:
      return (
        <Box>
          {clients.length === 0 && !loadingClients ? (
            renderNoOptionsMessage('clientes')
          ) : (
            <Autocomplete
              multiple
              disableCloseOnSelect={true}
              options={clients}
              getOptionLabel={option =>
                option.clientName || `Cliente ${option.id}`
              }
              value={selectedEntities}
              onChange={(_, newValue) => onEntitiesChange(newValue || [])}
              loading={loadingClients}
              renderInput={params => (
                <MuiTextField
                  {...params}
                  label='Seleccionar Clientes'
                  variant='outlined'
                  placeholder='Buscar y seleccionar múltiples clientes...'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingClients ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component='li' {...props}>
                  <Box>
                    <Typography variant='subtitle2'>
                      {option.clientName}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      Email: {option.clientEmail} • ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          )}
        </Box>
      );

    case EntityType.PLANT:
      return (
        <Box>
          {plants.length === 0 && !loadingPlants ? (
            renderNoOptionsMessage('plantas')
          ) : (
            <Autocomplete
              multiple
              disableCloseOnSelect={true}
              options={plants}
              getOptionLabel={option =>
                option.plantName || `Planta ${option.id}`
              }
              value={selectedEntities}
              onChange={(_, newValue) => onEntitiesChange(newValue || [])}
              loading={loadingPlants}
              renderInput={params => (
                <MuiTextField
                  {...params}
                  label='Seleccionar Plantas'
                  variant='outlined'
                  placeholder='Buscar y seleccionar múltiples plantas...'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPlants ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component='li' {...props}>
                  <Box>
                    <Typography variant='subtitle2'>
                      {option.plantName}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      Email: {option.clientEmail} • ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          )}
        </Box>
      );

    case EntityType.DASHBOARDGROUP:
      return (
        <Box>
          <HierarchicalFilter
            levels={getHierarchicalLevels(entityType)}
            onChange={onHierarchicalChange}
            initialValues={hierarchicalSelections}
            size='medium'
          />
          {hierarchicalSelections.plantId && (
            <Box sx={{ mt: 2 }}>
              {dashboardGroups.length === 0 && !loadingDashboardGroups ? (
                renderNoOptionsMessage('grupos de dashboard', 'en esta planta')
              ) : (
                <Autocomplete
                  multiple
                  disableCloseOnSelect={true}
                  options={dashboardGroups}
                  getOptionLabel={option =>
                    option.groupName || `Grupo ${option.id}`
                  }
                  value={selectedEntities}
                  onChange={(_, newValue) => onEntitiesChange(newValue || [])}
                  loading={loadingDashboardGroups}
                  disabled={!hierarchicalSelections.plantId}
                  renderInput={params => (
                    <MuiTextField
                      {...params}
                      label='Seleccionar Grupos de Dashboard'
                      variant='outlined'
                      placeholder='Seleccionar múltiples grupos...'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDashboardGroups ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component='li' {...props}>
                      <Box>
                        <Typography variant='subtitle2'>
                          {option.groupName}
                        </Typography>
                        <Typography variant='caption' color='textSecondary'>
                          Planta ID: {option.plantId} • ID: {option.id}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              )}
            </Box>
          )}
        </Box>
      );

    case EntityType.DASHBOARD:
      return (
        <Box>
          <HierarchicalFilter
            levels={getHierarchicalLevels(entityType)}
            onChange={onHierarchicalChange}
            initialValues={hierarchicalSelections}
            size='medium'
          />
          {hierarchicalSelections.plantId && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Grupo de Dashboard</InputLabel>
                <Select
                  value={selectedDashboardGroup || ''}
                  onChange={e =>
                    onDashboardGroupChange(e.target.value as number)
                  }
                  label='Grupo de Dashboard'
                  disabled={loadingDashboardGroups}
                >
                  {/* Nota: Aquí mostramos TODOS los grupos sin filtrar 
                      para permitir seleccionar dashboards de cualquier grupo */}
                  {dashboardGroups.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.groupName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {selectedDashboardGroup && (
            <Box sx={{ mt: 2 }}>
              {dashboards.length === 0 && !loadingDashboards ? (
                renderNoOptionsMessage('dashboards', 'en este grupo')
              ) : (
                <Autocomplete
                  multiple
                  disableCloseOnSelect={true}
                  options={dashboards}
                  getOptionLabel={option =>
                    option.dashboardName || `Dashboard ${option.id}`
                  }
                  value={selectedEntities}
                  onChange={(_, newValue) => onEntitiesChange(newValue || [])}
                  loading={loadingDashboards}
                  disabled={!selectedDashboardGroup}
                  renderInput={params => (
                    <MuiTextField
                      {...params}
                      label='Seleccionar Dashboards'
                      variant='outlined'
                      placeholder='Seleccionar múltiples dashboards...'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDashboards ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component='li' {...props}>
                      <Box>
                        <Typography variant='subtitle2'>
                          {option.dashboardName}
                        </Typography>
                        <Typography variant='caption' color='textSecondary'>
                          Grupo ID: {option.dashboardGroupId} • ID: {option.id}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              )}
            </Box>
          )}
        </Box>
      );

    case EntityType.ALERT:
      return (
        <Box>
          <HierarchicalFilter
            levels={getHierarchicalLevels(entityType)}
            onChange={onHierarchicalChange}
            initialValues={hierarchicalSelections}
            size='medium'
          />
          {hierarchicalSelections.zoneId && (
            <Box sx={{ mt: 2 }}>
              {alerts.length === 0 && !loadingAlerts ? (
                renderNoOptionsMessage('alertas', 'en esta zona')
              ) : (
                <Autocomplete
                  multiple
                  disableCloseOnSelect={true}
                  options={alerts}
                  getOptionLabel={option =>
                    option.name || `Alerta ${option.id}`
                  }
                  value={selectedEntities}
                  onChange={(_, newValue) => onEntitiesChange(newValue || [])}
                  loading={loadingAlerts}
                  disabled={!hierarchicalSelections.zoneId}
                  renderInput={params => (
                    <MuiTextField
                      {...params}
                      label='Seleccionar Alertas'
                      variant='outlined'
                      placeholder='Seleccionar múltiples alertas...'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingAlerts ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component='li' {...props}>
                      <Box>
                        <Typography variant='subtitle2'>
                          {option.name}
                        </Typography>
                        <Typography variant='caption' color='textSecondary'>
                          {option.description && `${option.description} • `}Zona
                          ID: {option.zoneId} • ID: {option.id}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              )}
            </Box>
          )}
        </Box>
      );

    case EntityType.DIAGRAM:
      return (
        <Box>
          <HierarchicalFilter
            levels={getHierarchicalLevels(entityType)}
            onChange={onHierarchicalChange}
            initialValues={hierarchicalSelections}
            size='medium'
          />
          {hierarchicalSelections.plantId && (
            <Box sx={{ mt: 2 }}>
              {diagrams.length === 0 && !loadingDiagrams ? (
                renderNoOptionsMessage('diagramas', 'en esta planta')
              ) : (
                <Autocomplete
                  multiple
                  disableCloseOnSelect={true}
                  options={diagrams}
                  getOptionLabel={option =>
                    option.name || `Diagrama ${option.id}`
                  }
                  value={selectedEntities}
                  onChange={(_, newValue) => onEntitiesChange(newValue || [])}
                  loading={loadingDiagrams}
                  disabled={!hierarchicalSelections.plantId}
                  renderInput={params => (
                    <MuiTextField
                      {...params}
                      label='Seleccionar Diagramas'
                      variant='outlined'
                      placeholder='Seleccionar múltiples diagramas...'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDiagrams ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component='li' {...props}>
                      <Box>
                        <Typography variant='subtitle2'>
                          {option.name}
                        </Typography>
                        <Typography variant='caption' color='textSecondary'>
                          {option.description && `${option.description} • `}
                          Planta ID: {option.plantId} • ID: {option.id}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              )}
            </Box>
          )}
        </Box>
      );

    default:
      return (
        <Typography color='error'>
          Tipo de entidad no soportado: {entityType}
        </Typography>
      );
  }
};
